import { Link } from "react-router-dom";
import { ArrowLeft, Train, Heart, ExternalLink, Database, Camera, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DepotSeal } from "@/components/DepotSeal";
import { BrandChip } from "@/components/BrandChip";

export default function About() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/5"
              asChild
            >
              <Link to="/" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div className="flex-1">
              <h1 className="font-serif font-bold text-lg text-white">About This Project</h1>
            </div>
            <BrandChip size={40} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 lg:py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="text-amber-500 mx-auto mb-6 flex justify-center animate-float">
            <DepotSeal size={112} />
          </div>
          <p className="text-[11px] text-amber-500 uppercase tracking-[0.32em] font-semibold mb-3">
            A Preservation Archive
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mb-4 text-white leading-tight">
            North American{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
              Depots &amp; Towers
            </span>
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            A community-driven archive of North America&apos;s historic railroad passenger depots
            and interlocking signal towers — the working architecture of the rail age.
          </p>
          <p className="text-sm text-amber-500 mt-4 font-semibold tracking-[0.2em] uppercase">
            Hosted by Railstream · in memoriam Jack Marshall
          </p>
        </div>

        {/* Tribute Section */}
        <Card className="mb-8 bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-400">
              <Heart className="w-5 h-5" />
              In Honor of Jack Marshall
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-white/70">
            <p>
              This project exists to preserve and continue the incredible work of <span className="text-amber-400 font-semibold">Jack Marshall</span>, 
              the creator of <a href="https://www.depotmaps.com" target="_blank" rel="noopener noreferrer" 
              className="text-amber-400 hover:underline font-medium">depotmaps.com</a>.
            </p>
            <p>
              For years, Jack meticulously documented over <span className="text-white font-bold">9,000 surviving railroad passenger depots</span> 
              across the United States. His work represents decades of dedication to preserving railroad history, 
              creating an invaluable resource for historians, railroad enthusiasts, and travelers alike.
            </p>
            <p>
              After many attempts to reach Jack over the years without success, we at Railstream decided 
              to preserve this important historical data and make it available in a modern, mobile-friendly format. 
              We hope this serves as a fitting tribute to his remarkable contribution to railroad history.
            </p>
            <div className="pt-4 border-t border-amber-500/20">
              <p className="text-white/50 italic">
                "My depot mapping project began as a way to display the pictures I've taken of passenger train stations..."
                <br />— Jack Marshall, depotmaps.com
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Source */}
        <Card className="mb-8 bg-[#111] border-white/5">
          <CardHeader className="border-b border-white/5">
            <CardTitle className="flex items-center gap-2 text-white">
              <Database className="w-5 h-5 text-amber-500" />
              Data Sources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl">
                <h4 className="font-semibold mb-3 text-white">Primary Source</h4>
                <p className="text-sm text-white/50 mb-3">
                  All depot location data was originally compiled by Jack Marshall at depotmaps.com, 
                  with information sourced from the RRSHS database.
                </p>
                <a 
                  href="https://www.depotmaps.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-amber-500 hover:underline inline-flex items-center gap-1"
                >
                  Visit Original Site <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="p-5 bg-white/[0.02] border border-white/5 rounded-xl">
                <h4 className="font-semibold mb-3 text-white">RRSHS</h4>
                <p className="text-sm text-white/50 mb-3">
                  The Railroad Station Historical Society maintains the national database of railroad depots 
                  that served as the foundation for this mapping project.
                </p>
                <a 
                  href="http://www.rrshs.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-amber-500 hover:underline inline-flex items-center gap-1"
                >
                  Visit RRSHS <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card className="mb-8 bg-[#111] border-white/5">
          <CardHeader className="border-b border-white/5">
            <CardTitle className="text-white">What We Offer</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Train className="w-7 h-7 text-emerald-400" />
                </div>
                <h4 className="font-semibold mb-2 text-white">9,000+ Depots</h4>
                <p className="text-sm text-white/50">
                  Comprehensive database of surviving railroad passenger depots across 48 states
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-7 h-7 text-blue-400" />
                </div>
                <h4 className="font-semibold mb-2 text-white">Photo Uploads</h4>
                <p className="text-sm text-white/50">
                  Community-powered photo gallery to document depots as they exist today
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-amber-400" />
                </div>
                <h4 className="font-semibold mb-2 text-white">Mobile Friendly</h4>
                <p className="text-sm text-white/50">
                  Designed for phone use while traveling to find depots along your route
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Marker Legend */}
        <Card className="mb-8 bg-[#111] border-white/5">
          <CardHeader className="border-b border-white/5">
            <CardTitle className="text-white">Map Marker Guide</CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <p className="text-sm text-white/50 mb-5">
              The color of each marker indicates the depot's status and photo availability:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                <div className="w-5 h-5 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50 shrink-0" />
                <div>
                  <p className="font-medium text-sm text-white">Green</p>
                  <p className="text-xs text-white/40">Original site, photo available</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                <div className="w-5 h-5 rounded-full bg-red-500 shadow-lg shadow-red-500/50 shrink-0" />
                <div>
                  <p className="font-medium text-sm text-white">Red</p>
                  <p className="text-xs text-white/40">Original site, no photo yet</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                <div className="w-5 h-5 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50 shrink-0" />
                <div>
                  <p className="font-medium text-sm text-white">Blue</p>
                  <p className="text-xs text-white/40">Relocated depot, photo available</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                <div className="w-5 h-5 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/50 shrink-0" />
                <div>
                  <p className="font-medium text-sm text-white">Yellow</p>
                  <p className="text-xs text-white/40">Relocated depot, no photo yet</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center py-10">
          <h3 className="font-serif text-3xl font-bold mb-4 text-white">Ready to Explore?</h3>
          <p className="text-white/50 mb-8 max-w-xl mx-auto">
            Discover historic railroad depots near you or plan your next road trip 
            around these beautiful pieces of American history.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="h-12 px-8 bg-amber-500 hover:bg-amber-600 text-black font-semibold" asChild>
              <Link to="/">
                <Train className="w-5 h-5 mr-2" />
                Explore the Map
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-8 border-white/20 text-white hover:bg-white/5" asChild>
              <Link to="/states">
                Browse by State
              </Link>
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Train className="w-5 h-5 text-amber-500" />
            <span className="font-serif font-bold text-white">North American Depots & Towers</span>
          </div>
          <p className="text-sm text-white/30">
            Hosted by Railstream • In tribute to Jack Marshall's depotmaps.com
          </p>
        </div>
      </footer>
    </div>
  );
}
